import { DirectIndexSessionProvider } from "~/features/direct-index-builder/session-provider";
import {
  isPublicBrowseInitialPathname,
  isStartRuntimeRequired,
} from "./initial-route-preload";
import { isServerRendering } from "~/lib/ssr/runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AppUiProviders } from "~/components/app-providers";
import { AppAccountingPrewarmer } from "~/components/app-accounting-prewarmer";
import { LazyPrivyAppProvider } from "~/components/lazy-privy-app-provider";
import {
  GuestAppAuthProvider,
  SiweAuthProvider,
  SyntheticDevAuthProvider,
  useAppAuth,
} from "~/lib/auth/app-auth";
import { CurrentUserIdentityController } from "~/lib/auth/canonical-user-identity";
import {
  useSiweAuthProfile,
  useSyntheticDevAuthProfile,
  type SiweAuthProfile,
  type SyntheticDevAuthProfile,
} from "~/lib/auth/app-auth-runtime";
import { useVmPreference } from "~/lib/auth/vm-preference";
import { isExecutionStreamBrowserE2EMode } from "~/lib/e2e/is-execution-stream-browser-e2e-mode";
import { isMarketDataStreamBrowserE2EMode } from "~/lib/e2e/is-market-data-stream-browser-e2e-mode";
import { isWalletlessBrowserE2EMode } from "~/lib/e2e/is-walletless-browser-e2e-mode";
import { getPrivyAppId } from "~/lib/env";
import {
  hasPrivyOAuthCallback,
  hasPrivySessionHint,
} from "~/lib/auth/privy-session-hint";
import { getUserFeatureFlagsQueryOptions } from "~/lib/feature-flags";
import { LocaleBoundary } from "~/lib/language-lens";
import { useAppSessionScope } from "~/lib/app-session-scope";
import { initializeBrowserQueryPersistence } from "~/lib/query-persistence";
import { createRouterRuntimeContext } from "~/lib/router-runtime";
import { schedulePostPaint } from "~/lib/schedule-post-paint";
import { TelemetryRuntimeController } from "~/lib/telemetry/runtime-controller";
import { getAppAuthTransportAuthHeadersForSnapshot } from "~/lib/auth/app-auth-runtime";
import {
  getBrowserPlatformApiAccountingClient,
  getBrowserPlatformApiClient,
  PORTFOLIO_ACCOUNTING_REQUEST_TIMEOUT_MS,
} from "~/lib/trpc/browser-client";
import { createBrowserPlatformApiTrpcAdapter } from "~/lib/trpc/platform-api-adapter";
import {
  createUiConfigTrpcSyncClient,
  quarantineUiConfigOverlayForPrincipal,
  useUiConfigServerSync,
} from "~/lib/ui-config";

export type AppProviderMode = "guest" | "privy" | "siwe" | "synthetic";

const BROWSER_QUERY_PERSISTENCE_FALLBACK_DELAY_MS = 250;
/**
 * Static/marketing paths with nothing to restore. Persistence is skipped
 * entirely here: opening IndexedDB would only cost main-thread time on the
 * page least likely to need it.
 */
const QUERY_PERSISTENCE_READY_EXACT_PATHS = [
  "/",
  "/start",
  "/api",
  "/developers",
  "/platform",
  "/policy",
  "/terms",
] as const;
/**
 * Paths whose fold is persisted market data (prices, movers, market status,
 * symbol identity). These hydrate IMMEDIATELY instead of `schedulePostPaint`,
 * so `preloadStocksRoute` can await the restored cache rather than racing it
 * to the network — the whole point of persisting the fold. `/stocks/:symbol`
 * matches through the `/stocks/` prefix.
 */
const QUERY_PERSISTENCE_EAGER_HYDRATION_EXACT_PATHS = [
  "/build",
  "/explore",
  "/stocks",
] as const;
const QUERY_PERSISTENCE_EAGER_HYDRATION_PATH_PREFIXES = ["/stocks/"] as const;

const AppDepositMethodOptionsCacheWarmer = lazy(async () => {
  const module =
    await import("~/components/app-deposit-method-options-cache-warmer");
  return { default: module.AppDepositMethodOptionsCacheWarmer };
});

export interface ResolveAppProviderModeInput {
  hasPrivyConfig: boolean;
  isExecutionStreamBrowserSystemMode: boolean;
  isMarketDataStreamBrowserSystemMode: boolean;
  isWalletlessBrowserSystemMode: boolean;
  pathname: string;
  siweProfile: SiweAuthProfile | null;
  syntheticProfile: SyntheticDevAuthProfile | null;
}

export interface ResolvedAppProvidersProps extends Partial<ResolveAppProviderModeInput> {
  children?: ReactNode;
}

export function isQueryPersistenceReadyBootstrapPathname(
  pathname: string,
): boolean {
  return QUERY_PERSISTENCE_READY_EXACT_PATHS.includes(
    pathname as (typeof QUERY_PERSISTENCE_READY_EXACT_PATHS)[number],
  );
}

export function isQueryPersistenceEagerHydrationPathname(
  pathname: string,
): boolean {
  return (
    QUERY_PERSISTENCE_EAGER_HYDRATION_EXACT_PATHS.includes(
      pathname as (typeof QUERY_PERSISTENCE_EAGER_HYDRATION_EXACT_PATHS)[number],
    ) ||
    QUERY_PERSISTENCE_EAGER_HYDRATION_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    )
  );
}

export function resolveAppProviderMode(
  input: ResolveAppProviderModeInput,
): AppProviderMode {
  if (
    input.isWalletlessBrowserSystemMode ||
    input.isExecutionStreamBrowserSystemMode ||
    input.isMarketDataStreamBrowserSystemMode ||
    input.pathname === "/design-system" ||
    // Public shared receipts (/r/:slug) are anonymous by design: their data
    // path is the public rate-limited endpoint, so don't boot the auth SDK
    // for link visitors.
    input.pathname.startsWith("/r/")
  ) {
    return "guest";
  }

  if (input.siweProfile) {
    return "siwe";
  }

  if (
    input.syntheticProfile &&
    (import.meta.env.DEV || import.meta.env.MODE === "test")
  ) {
    return "synthetic";
  }

  if (input.hasPrivyConfig) {
    return "privy";
  }

  return "guest";
}

export function AppRouterRuntimeProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { authenticated, ready, transport, user } = useAppAuth();
  const { isLoaded, preference } = useVmPreference();
  const pathname = useRouterPathname();
  const [, setQueryPersistenceReady] = useState(() =>
    isQueryPersistenceReadyBootstrapPathname(pathname),
  );
  const { platformApi, accountingPlatformApi } = useMemo(
    () => {
      if (isServerRendering()) {
        return {
          platformApi: getBrowserPlatformApiClient(),
          accountingPlatformApi: getBrowserPlatformApiAccountingClient(),
        };
      }
      const authRuntime = {
        getAuthHeaders: async () =>
          await getAppAuthTransportAuthHeadersForSnapshot(transport),
        getSnapshot: () => transport,
      };
      return {
        platformApi: createBrowserPlatformApiTrpcAdapter({
          authRuntime,
          batch: true,
          getVmPreference: () => preference,
        }),
        accountingPlatformApi: createBrowserPlatformApiTrpcAdapter({
          authRuntime,
          batch: false,
          getVmPreference: () => preference,
          requestTimeoutMs: PORTFOLIO_ACCOUNTING_REQUEST_TIMEOUT_MS,
        }),
      };
    },
    [preference, transport],
  );
  const context = useMemo(
    () => {
      const nextContext = createRouterRuntimeContext({
        accountingPlatformApi,
        authenticated,
        platformApi,
        queryClient,
        ready,
        user,
        vmPreference: preference,
        vmPreferenceLoaded: isLoaded,
      });
      return {
        ...nextContext,
        canPrewarmAccounting: () => {
          const current = router.options.context;
          return current.accountingPlatformApi === accountingPlatformApi &&
            current.auth.sessionQueryScope.scopeToken === nextContext.auth.sessionQueryScope.scopeToken &&
            current.auth.ready && current.auth.authenticated && current.auth.vmPreferenceLoaded;
        },
      };
    },
    [
      accountingPlatformApi,
      authenticated,
      isLoaded,
      platformApi,
      preference,
      queryClient,
      ready,
      router,
      user,
    ],
  );
  // Auth becomes available after hydration; preserve the request DB client.
  useEffect(() => {
    router.update({ context: { ...router.options.context, ...context } });
    void router.invalidate();
  }, [context, router]);
  const sessionScope = useAppSessionScope();
  const uiConfigPlatformApi = useMemo(() => getBrowserPlatformApiClient(), []);
  const { data: uiConfigFeatureFlags } = useQuery({
    ...getUserFeatureFlagsQueryOptions({
      authQueryScope: sessionScope.authScope,
      platformApi: uiConfigPlatformApi,
    }),
    enabled: ready && isLoaded && authenticated,
  });
  const uiConfigSyncClient = useMemo(
    () => createUiConfigTrpcSyncClient(uiConfigPlatformApi),
    [uiConfigPlatformApi],
  );
  const authUserId = user?.id ?? null;

  // This controller must stay outside LocaleBoundary: remounting it can cancel
  // a pending first preference push and let the replacement adopt stale remote
  // state before that explicit choice reaches the server.
  useLayoutEffect(() => {
    if (authUserId !== null) {
      quarantineUiConfigOverlayForPrincipal(authUserId);
    }
  }, [authUserId]);
  useUiConfigServerSync({
    client: uiConfigSyncClient,
    enabled:
      authenticated &&
      uiConfigFeatureFlags?.flags["addon_platform_ui_config"] === true,
    // Keyed by USER id, never a wallet address — wallets vary across VMs
    // for the same user; config identity must not.
    principalKey: authUserId,
  });

  return (
    <>
      <BrowserQueryPersistenceController
        onReadyChange={setQueryPersistenceReady}
        pathname={pathname}
      />
      <CurrentUserIdentityController context={context} />
      <TelemetryRuntimeController context={context} pathname={pathname} />
      <Suspense fallback={null}>
        {ready && authenticated ? (
          <AppDepositMethodOptionsCacheWarmer pathname={pathname} />
        ) : null}
      </Suspense>
      <LocaleBoundary>{children}</LocaleBoundary>
      {ready && authenticated && isLoaded ? (
        <AppAccountingPrewarmer context={context} />
      ) : null}
    </>
  );
}

function BrowserQueryPersistenceController(props: {
  onReadyChange: (ready: boolean) => void;
  pathname: string;
}) {
  const queryClient = useQueryClient();
  const hasInitializedRef = useRef(false);
  const { onReadyChange, pathname } = props;

  useEffect(() => {
    if (isQueryPersistenceReadyBootstrapPathname(pathname)) {
      onReadyChange(true);
      return;
    }

    if (hasInitializedRef.current) {
      onReadyChange(true);
      return;
    }

    onReadyChange(false);
    let cancelled = false;
    const startInitialization = () => {
      if (hasInitializedRef.current) {
        return;
      }

      hasInitializedRef.current = true;
      void initializeBrowserQueryPersistence(queryClient).finally(() => {
        if (!cancelled) {
          onReadyChange(true);
        }
      });
    };

    // The fold routes read their prices back out of the persisted cache, and
    // their loader waits (briefly) on this. Deferring the restore past paint
    // there would guarantee the loader loses that race, so those routes pay
    // the IndexedDB open up front; everything else still hydrates post-paint.
    if (isQueryPersistenceEagerHydrationPathname(pathname)) {
      startInitialization();
      return () => {
        cancelled = true;
      };
    }

    const cancelSchedule = schedulePostPaint(
      startInitialization,
      BROWSER_QUERY_PERSISTENCE_FALLBACK_DELAY_MS,
    );

    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [onReadyChange, pathname, queryClient]);

  return null;
}

function useRouterPathname(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

function useResolvedProviderPathname(
  explicitPathname: string | undefined,
): string {
  const routerPathname = useRouterPathname();

  return explicitPathname ?? routerPathname;
}

export function ResolvedAppProviders(props: ResolvedAppProvidersProps = {}) {
  const pathname = useResolvedProviderPathname(props.pathname);
  return (
    <DirectIndexSessionProvider pathname={pathname}>
      <ResolvedAppProviderRuntime {...props} />
    </DirectIndexSessionProvider>
  );
}

function ResolvedAppProviderRuntime(props: ResolvedAppProvidersProps) {
  const runtimeSyntheticProfile = useSyntheticDevAuthProfile();
  const runtimeSiweProfile = useSiweAuthProfile();
  const siweProfile = props.siweProfile ?? runtimeSiweProfile;
  const syntheticProfile = props.syntheticProfile ?? runtimeSyntheticProfile;
  const pathname = useResolvedProviderPathname(props.pathname);
  const searchStr = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const onboardingRuntimeRequired = isStartRuntimeRequired(pathname, searchStr);
  const mode = resolveAppProviderMode({
    hasPrivyConfig: props.hasPrivyConfig ?? getPrivyAppId() !== null,
    isExecutionStreamBrowserSystemMode:
      props.isExecutionStreamBrowserSystemMode ??
      isExecutionStreamBrowserE2EMode(),
    isMarketDataStreamBrowserSystemMode:
      props.isMarketDataStreamBrowserSystemMode ??
      isMarketDataStreamBrowserE2EMode(),
    isWalletlessBrowserSystemMode:
      props.isWalletlessBrowserSystemMode ?? isWalletlessBrowserE2EMode(),
    pathname,
    siweProfile,
    syntheticProfile,
  });

  switch (mode) {
    case "siwe":
      return (
        <SiweAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider>
              {props.children}
            </AppRouterRuntimeProvider>
          </AppUiProviders>
        </SiweAuthProvider>
      );
    case "synthetic":
      return (
        <SyntheticDevAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider>
              {props.children}
            </AppRouterRuntimeProvider>
          </AppUiProviders>
        </SyntheticDevAuthProvider>
      );
    case "privy":
      return (
        <LazyPrivyAppProvider
          pathname={pathname}
          publicBootstrap={isPublicBrowseInitialPathname(pathname)}
          runtimeRequired={onboardingRuntimeRequired}
          mountRuntimeAfterPublicBootstrap={
            isPublicBrowseInitialPathname(pathname) &&
            (onboardingRuntimeRequired ||
              hasPrivySessionHint() ||
              hasPrivyOAuthCallback())
          }
        >
          <AppUiProviders>
            <AppRouterRuntimeProvider>
              {props.children}
            </AppRouterRuntimeProvider>
          </AppUiProviders>
        </LazyPrivyAppProvider>
      );
    default:
      return (
        <GuestAppAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider>
              {props.children}
            </AppRouterRuntimeProvider>
          </AppUiProviders>
        </GuestAppAuthProvider>
      );
  }
}
